import type { ButtonHTMLAttributes, PropsWithChildren, ReactNode } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { AlertTriangle, CheckCircle2, Info, LoaderCircle, X, XCircle } from 'lucide-react'

export function Button({ className = '', variant = 'default', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'default' | 'primary' | 'danger' | 'ghost' }) {
  return <button className={`btn btn-${variant} ${className}`} {...props} />
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return <div className="page-header">
    <div><h1>{title}</h1>{subtitle && <p>{subtitle}</p>}</div>
    {actions && <div className="page-actions">{actions}</div>}
  </div>
}

export function Card({ children, className = '' }: PropsWithChildren<{ className?: string }>) {
  return <div className={`card ${className}`}>{children}</div>
}

export function LoadingBlock({ label = 'Загружаем данные…' }: { label?: string }) {
  return <div className="loading-block"><LoaderCircle size={22} className="spin"/><span>{label}</span></div>
}

export function EmptyState({ title, text, icon }: { title: string; text?: string; icon?: ReactNode }) {
  return <div className="empty-state">{icon}<strong>{title}</strong>{text && <span>{text}</span>}</div>
}

export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  return <div className="error-state"><XCircle size={22}/><div><strong>Не получилось загрузить данные</strong><span>{error.message}</span></div>{onRetry && <Button onClick={onRetry}>Повторить</Button>}</div>
}

export function Badge({ children, tone = 'neutral' }: PropsWithChildren<{ tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'info' }>) {
  return <span className={`badge badge-${tone}`}>{children}</span>
}

export function severityTone(value?: string): 'neutral' | 'good' | 'warn' | 'bad' | 'info' {
  if (value === 'error' || value === 'blocked' || value === 'rejected') return 'bad'
  if (value === 'warning' || value === 'in_progress' || value === 'pending') return 'warn'
  if (value === 'info') return 'info'
  if (value === 'verified' || value === 'accepted' || value === 'extracted' || value === 'ok') return 'good'
  return 'neutral'
}

export function Modal({ open, title, subtitle, children, onClose, wide = false }: PropsWithChildren<{ open: boolean; title: string; subtitle?: string; onClose: () => void; wide?: boolean }>) {
  return <AnimatePresence>
    {open && <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <motion.div className={`modal ${wide ? 'modal-wide' : ''}`} initial={{ opacity: 0, y: 18, scale: .985 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: .99 }} transition={{ duration: .18 }}>
        <div className="modal-head"><div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div><button className="icon-btn" onClick={onClose} aria-label="Закрыть"><X size={18}/></button></div>
        <div className="modal-body">{children}</div>
      </motion.div>
    </motion.div>}
  </AnimatePresence>
}

export function Notice({ tone = 'info', title, children }: PropsWithChildren<{ tone?: 'info' | 'warn' | 'good' | 'bad'; title?: string }>) {
  const Icon = tone === 'good' ? CheckCircle2 : tone === 'warn' ? AlertTriangle : tone === 'bad' ? XCircle : Info
  return <div className={`notice notice-${tone}`}><Icon size={18}/><div>{title && <strong>{title}</strong>}<span>{children}</span></div></div>
}
